import SEO from '../components/SEO';
import './SimplePage.css';
import ParticleCanvas from '../components/ui/ParticleCanvas';

const RELEASES = [
  {
    version: 'v2.1.0',
    date: 'Apr 14, 2026',
    tag: 'New Features',
    tagColor: '#6366F1',
    items: [
      { type: 'new', text: 'WooCommerce webhook integration for automatic order confirmations' },
      { type: 'new', text: 'LeadFlow pipeline: 6-stage AI-powered sales CRM' },
      { type: 'new', text: 'Bulk CSV import for contacts with auto lead-stage assignment' },
      { type: 'improved', text: 'AI response speed reduced from 1.2s to under 0.4s average' },
      { type: 'improved', text: 'Knowledge base now supports up to 100,000 characters on Pro plan' },
      { type: 'fixed', text: 'Fixed issue where broadcast messages were not delivered to recently added contacts' },
    ],
  },
  {
    version: 'v2.0.0',
    date: 'Mar 10, 2026',
    tag: 'Major Release',
    tagColor: '#16A34A',
    items: [
      { type: 'new', text: 'Complete dashboard redesign with dark mode support' },
      { type: 'new', text: 'AI Email Assistant for Gmail: reads incoming email and drafts replies for your review' },
      { type: 'new', text: 'Multi-agent inbox: unlimited team members per channel' },
      { type: 'new', text: 'Human takeover with one-click AI pause and resume' },
      { type: 'improved', text: 'Shopify OAuth connection flow simplified to 3 steps' },
    ],
  },
  {
    version: 'v1.5.2',
    date: 'Feb 20, 2026',
    tag: 'Bug Fixes',
    tagColor: '#DC2626',
    items: [
      { type: 'fixed', text: 'Fixed Shopify order webhook timeout causing missed confirmations' },
      { type: 'fixed', text: 'Resolved issue with Arabic and RTL language rendering in chat UI' },
      { type: 'fixed', text: 'Fixed broadcast analytics showing incorrect open rate percentages' },
      { type: 'improved', text: 'Improved QR code stability — WhatsApp connection now persists 3x longer' },
    ],
  },
  {
    version: 'v1.5.0',
    date: 'Feb 1, 2026',
    tag: 'New Features',
    tagColor: '#6366F1',
    items: [
      { type: 'new', text: 'Shopify order confirmation pipeline with YES/NO intent detection' },
      { type: 'new', text: 'Configurable follow-up sequences at 30min, 2hr, and 24hr intervals' },
      { type: 'new', text: 'Auto-cancel orders after follow-up exhaustion with Shopify status sync' },
      { type: 'new', text: 'Broadcast scheduling: set messages to send at a future date and time' },
      { type: 'improved', text: 'AI now supports 12 languages including Arabic, Urdu, and Spanish' },
    ],
  },
  {
    version: 'v1.0.0',
    date: 'Jan 1, 2026',
    tag: 'Launch',
    tagColor: '#D97706',
    items: [
      { type: 'new', text: 'Initial public launch of Zepliq' },
      { type: 'new', text: 'WhatsApp connection via QR code scan (no API required)' },
      { type: 'new', text: 'AI auto-reply with custom knowledge base training' },
      { type: 'new', text: 'Contact management with tags and conversation history' },
      { type: 'new', text: 'Broadcast campaigns with delivery tracking' },
    ],
  },
];

const TYPE_STYLES = {
  new:      { label: 'New',      bg: 'rgba(99,102,241,0.12)',  color: '#6366F1' },
  improved: { label: 'Improved', bg: 'rgba(22,163,74,0.12)',   color: '#16A34A' },
  fixed:    { label: 'Fixed',    bg: 'rgba(220,38,38,0.12)',   color: '#DC2626' },
};

export default function ChangelogPage() {
  return (
    <div className="simple-page">
      <SEO
        title="Changelog — What's New in Zepliq"
        description="See everything new in Zepliq: new features, improvements, and bug fixes. We ship fast — check back weekly for the latest updates to your WhatsApp automation platform."
        keywords="Zepliq changelog, Zepliq updates, WhatsApp automation new features, Zepliq release notes, what's new Zepliq"
        canonical="/changelog"
      />

      <section className="simple-hero simple-hero--sm">
        <ParticleCanvas className="simple-hero__canvas" />
        <div className="simple-hero__blob simple-hero__blob--1" aria-hidden="true" />
        <div className="simple-hero__blob simple-hero__blob--2" aria-hidden="true" />
        <div className="container simple-hero__inner">
          <span className="badge badge-brand">Changelog</span>
          <h1 className="display-xl simple-hero__title">
            What's new in<br />
            <span className="gradient-text">Zepliq.</span>
          </h1>
          <p className="text-lg simple-hero__sub">
            Every update, improvement, and fix — all in one place.
          </p>
        </div>
      </section>

      <section className="simple-section section">
        <div className="container">
          <div className="changelog">
            {RELEASES.map(release => (
              <div key={release.version} className="changelog-item">
                <div className="changelog-item__meta">
                  <span className="changelog-item__version">{release.version}</span>
                  <span className="changelog-item__date">{release.date}</span>
                  <span className="changelog-item__tag" style={{ background: release.tagColor + '22', color: release.tagColor }}>
                    {release.tag}
                  </span>
                </div>
                <div className="changelog-item__body">
                  {release.items.map((item, i) => {
                    const style = TYPE_STYLES[item.type];
                    return (
                      <div key={i} className="changelog-entry">
                        <span className="changelog-entry__badge" style={{ background: style.bg, color: style.color }}>
                          {style.label}
                        </span>
                        <p className="changelog-entry__text">{item.text}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
